/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-08-06 08:34:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, getFormValues, change } from 'redux-form';
import { FormLayout, TextStyle, Link, Button, Stack, Label } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import Preview from './preview';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import RenderColorPickerHexPopover from 'src/components/views/common/render-color-picker-hex-popover';
import { PAGE_LAYOUT_TYPE, VIEW_AS_TYPE } from 'src/constants/theme-settings';
import { buildOptionList } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = WithI18nProps;

type State = {
  pageLayoutType: Array<{ value: string, label: string }>,
  viewAsType: Array<{ value: string, label: string }>,
  showCollectionHeaderSection: true,
  showToolbarSection: true
};

class GeneralTab extends Component<Props, State> {
  state = {
    pageLayoutType: [],
    viewAsType: [],
    showCollectionHeaderSection: true,
    showToolbarSection: true
  };

  componentDidMount() {
    const { i18n } = this.props;

    const pageLayoutType = buildOptionList(PAGE_LAYOUT_TYPE, i18n);
    const viewAsType = buildOptionList(VIEW_AS_TYPE, i18n);
    this.setState({
      pageLayoutType,
      viewAsType
    });
  }

  componentWillReceiveProps(nextProps) {
    const { i18n } = nextProps;
    const { i18n: prevI18n } = this.props;

    if (i18n.locale !== prevI18n.locale) {
      const pageLayoutType = buildOptionList(PAGE_LAYOUT_TYPE, i18n);
      const viewAsType = buildOptionList(VIEW_AS_TYPE, i18n);
      this.setState({
        pageLayoutType,
        viewAsType
      });
    }
  }

  render() {
    const { collectionHeaderBg, collectionHeaderText, updateField, i18n } = this.props;
    const { pageLayoutType, viewAsType, showCollectionHeaderSection, showToolbarSection } =
      this.state;

    return (
      <div className="BC-Default-Theme-Settings__General">
        <div className="settings-content">
          <FormLayout>
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.DefaultThemeSettings.GeneralTab.helperText', {
                t1: (
                  <Link external url={`${URL['DefaultThemeSettingsDoc']}#a1`}>
                    {i18n.translate('AdminHTML.DefaultThemeSettings.GeneralTab.helperText_t1')}
                  </Link>
                )
              })}
            </TextStyle>

            <Field
              name="general.layoutType"
              component={RenderChoice}
              label={i18n.translate('AdminHTML.DefaultThemeSettings.GeneralTab.field1_Label')}
              choices={pageLayoutType}
            />

            <div className="breakLine" />
            <div className="groupFields-label">
              <Button
                disclosure={showCollectionHeaderSection ? 'down' : 'up'}
                plain
                monochrome
                removeUnderline
                onClick={() => this.toggleState('showCollectionHeaderSection')}>
                <TextStyle variation="strong">
                  {i18n.translate(
                    'AdminHTML.DefaultThemeSettings.GeneralTab.gFCollectionHeader_Label'
                  )}
                </TextStyle>
              </Button>
            </div>

            {showCollectionHeaderSection && (
              <div className="groupFields-content">
                <div style={{ marginTop: '-1.5rem' }}>
                  <Stack vertical>
                    <Field
                      name="general.showCollectionImage"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field2_Label'
                      )}
                    />
                    <Field
                      name="general.showCollectionDes"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field3_Label'
                      )}
                    />

                    <RenderColorPickerHexPopover
                      colorValue={collectionHeaderBg}
                      fieldName="general.collectionHeaderBg"
                      fieldLabel={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field4_Label'
                      )}
                      updateField={updateField}
                    />

                    <RenderColorPickerHexPopover
                      colorValue={collectionHeaderText}
                      fieldName="general.collectionHeaderText"
                      fieldLabel={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field5_Label'
                      )}
                      updateField={updateField}
                    />
                  </Stack>
                </div>
              </div>
            )}

            <div className="breakLine" />

            <div className="groupFields-label">
              <Button
                disclosure={showToolbarSection ? 'down' : 'up'}
                plain
                monochrome
                removeUnderline
                onClick={() => this.toggleState('showToolbarSection')}>
                <TextStyle variation="strong">
                  {i18n.translate('AdminHTML.DefaultThemeSettings.GeneralTab.gFToolbar_Label')}
                </TextStyle>
              </Button>
            </div>

            {showToolbarSection && (
              <div className="groupFields-content">
                <div style={{ marginTop: '-1rem' }}>
                  <div className="sectionLabel">
                    <Label>
                      {i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.gFToolbarDesktop_Label'
                      )}
                    </Label>
                  </div>
                  <Stack vertical>
                    <Field
                      name="general.showViewAs"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field6_Label'
                      )}
                    />
                    <Field
                      name="general.showProductCount"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field7_Label'
                      )}
                    />
                    <Field
                      name="general.showSortBy"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field8_Label'
                      )}
                    />
                  </Stack>
                </div>
                <div>
                  <div className="sectionLabel">
                    <Label>
                      {i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.gFToolbarMobile_Label'
                      )}
                    </Label>
                  </div>
                  <Stack vertical>
                    <Field
                      name="general.showViewAsM"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field9_Label'
                      )}
                    />
                    <Field
                      name="general.showProductCountM"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field10_Label'
                      )}
                    />
                    <Field
                      name="general.showSortByM"
                      component={RenderCheckbox}
                      label={i18n.translate(
                        'AdminHTML.DefaultThemeSettings.GeneralTab.field11_Label'
                      )}
                    />
                  </Stack>
                </div>
                <Field
                  name="general.viewAsType"
                  component={RenderChoice}
                  label={i18n.translate('AdminHTML.DefaultThemeSettings.GeneralTab.field12_Label')}
                  choices={viewAsType}
                />
              </div>
            )}
          </FormLayout>
        </div>
        <div className="preview">
          <Preview />
        </div>
      </div>
    );
  }

  toggleState = (fieldName: string) => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };
}

const mapStateToProps = (state, props) => {
  const defaultThemeSettingsForm = getFormValues('DefaultThemeSettingsForm')(state);

  let colorList = {};
  ['collectionHeaderBg', 'collectionHeaderText'].map(item => {
    colorList[item] = _.get(defaultThemeSettingsForm, `general.${item}`, '#FFFFFF');
    return '';
  });

  return {
    ...colorList
  };
};

const mapDispatchToProps = dispatch => ({
  updateField: (id, data) => dispatch(change(`DefaultThemeSettingsForm`, id, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(GeneralTab));
